<template>
  <section class="screen">
    <step-header step="capture-video" :hidden="editing" />

    <div class="s-body stack gap-10">
      <!-- Guidance stays up before and during recording: the phrase has to be readable
           while the user is reading it out loud. -->
      <div v-if="phase !== 'done'" class="actguide">
        <div class="combo">
          <span class="ar l"><base-icon name="arrow-left" :width="2.5" /></span>
          <span class="face">
            <img src="/files/capture_video_Character.gif" alt="راهنمای چرخاندن سر" />
          </span>
          <span class="ar r"><base-icon name="arrow-right" :width="2.5" /></span>
        </div>

        <div class="ag-line">هم‌زمان سرتان را بچرخانید و متن زیر را بخوانید:</div>

        <div class="ag-phrase">
          <div v-if="challenge" class="ag-val">
            <span class="pq">«</span> {{ challenge }} <span class="pq">»</span>
          </div>
          <div v-else-if="challengeError" class="ag-error">
            <span>دریافت متن با خطا مواجه شد.</span>
            <button type="button" class="linkbtn" @click="loadChallenge">تلاش مجدد</button>
          </div>
          <div v-else class="ag-val ag-loading">در حال دریافت متن…</div>
        </div>
      </div>

      <video-recorder
        ref="recorder"
        :active="cameraActive"
        :guide="!manualUnlocked"
        @framing="onFraming"
        @ready="onCameraReady"
        @denied="onCameraDenied"
        @framed="onFramed"
        @unavailable="onDetectorUnavailable"
        @lock="saveLock = $event"
        @phase="phase = $event"
        @complete="onComplete"
      />

      <div v-if="manualUnlocked && phase === 'idle'" class="helper">
        <base-icon name="sun" />
        <span>تشخیص خودکار چهره ممکن نشد؛ با رعایت راهنمای بالا ضبط را شروع کنید.</span>
      </div>

      <div v-if="phase === 'done'" class="stack gap-6 center" style="margin-top: 14px">
        <h2>آیا ویدیو واضح است؟</h2>
        <p>ویدیوی خود را ببینید؛ اگر واضح ضبط شده ادامه دهید، در غیر این صورت دوباره ضبط کنید.</p>
      </div>
    </div>

    <div class="s-foot">
      <button
        v-if="phase === 'idle'"
        class="btn btn-primary recbtn"
        type="button"
        :disabled="!canStart"
        :aria-describedby="canStart ? null : 'record-help'"
        @click="start"
      >
        <base-icon name="record" />
        <span>شروع ضبط</span>
      </button>

      <button
        v-else-if="phase === 'recording'"
        class="btn btn-primary recbtn"
        type="button"
        :disabled="saveLock > 0"
        @click="stop"
      >
        {{ saveLock > 0 ? `ذخیره کردن ویدیو (${toFa(saveLock)})` : "ذخیره کردن ویدیو" }}
      </button>

      <template v-else>
        <button class="btn btn-primary" type="button" :disabled="!recorded" @click="confirm">
          تأیید و ادامه
        </button>
        <button class="btn btn-outline" type="button" @click="retry">
          <base-icon name="refresh" />
          <span>ضبط مجدد</span>
        </button>
      </template>

      <!-- See capture-image: the reason the button is dead has to be readable. Outside the
           v-if chain above, which it would otherwise break. -->
      <span id="record-help" class="sr-only">{{ framingHint }}</span>
    </div>

    <permission-gate :state="gate" :busy="requesting" @grant="grant" @retry="grant" />
  </section>
</template>

<script>
import StepHeader from "@/components/StepHeader.vue";
import VideoRecorder from "@/components/VideoRecorder.vue";
import PermissionGate from "@/components/PermissionGate.vue";
import BaseIcon from "@/components/BaseIcon.vue";
import { FLOW_ORDER, FRAMING_FALLBACK_MS } from "@/constants";
import { mediaAccessGranted } from "@/utils/device";
import { toFa } from "@/utils/digits";
import { playReadyCue } from "@/utils/cue";

/**
 * Step 2 — the spoken clip.
 *
 * The user turns their head while reading a phrase the backend supplies, so recording
 * cannot start until that phrase has arrived: a countdown with nothing to read would
 * produce a clip that fails verification.
 *
 * The recorder component owns the hardware and the phases; this view owns the buttons,
 * the challenge text, and what happens next.
 */
export default {
  name: "CaptureVideoView",
  components: { StepHeader, VideoRecorder, PermissionGate, BaseIcon },

  data() {
    return {
      /** "closed" | "priming" | "blocked" */
      gate: "closed",
      cameraActive: false,
      cameraReady: false,
      requesting: false,
      /** The user has tapped "allow" on our own sheet at least once on this screen. */
      primed: false,
      challengeError: false,
      /** Seconds until saving is allowed; mirrors the recorder's own lock. */
      saveLock: 0,
      recorded: false,
      /** "idle" | "recording" | "done", mirrored from the recorder's `phase` event. */
      phase: "idle",
      /** A face is steadily inside the guide oval. */
      framed: false,
      /** The framing check never succeeded here, so recording was handed back. */
      manualUnlocked: false,
      /** The current instruction, mirrored here so assistive tech can read it. */
      framingHint: "",
    };
  },

  computed: {
    editing() {
      return this.$route.query.edit === "1";
    },
    challenge() {
      return this.$store.state.flow.challengeSequence;
    },

    /**
     * Recording starts only once the face is framed — the same gate as the photo step, for
     * the same reason: a clip that starts with the user out of frame is a clip that gets
     * rejected after they have already read the phrase out loud.
     */
    canStart() {
      return this.cameraReady && !!this.challenge && (this.framed || this.manualUnlocked);
    },
  },

  async created() {
    // Not reactive: nothing renders from these.
    this.fallbackTimer = null;
    this.cued = false;
    this.loadChallenge();

    // The face step already asked for camera *and* microphone with the same constraints,
    // so by the time the user gets here the browser has nothing left to prompt for.
    // Asking again would be a sheet in front of a prompt that never appears.
    if (this.$store.state.ui.mediaGranted || (await mediaAccessGranted())) {
      this.requestAccess();
    } else {
      this.gate = "priming";
    }
  },

  beforeDestroy() {
    this.cancelManualFallback();
  },

  methods: {
    toFa,

    /** The user tapped a button on our sheet; from here a denial is a real denial. */
    grant() {
      this.primed = true;
      this.requestAccess();
    },

    /**
     * Sound the cue when framing becomes correct — but never once recording has started.
     *
     * The microphone is live during the clip, so a beep from the phone would be recorded
     * into the footage the backend has to verify, over the phrase the user is reading.
     */
    onFraming(state) {
      this.framingHint = state.hint;
      // The first verdict is what starts the clock: before it, the runtime may still have
      // been downloading, and that wait is not the user failing to frame themselves.
      if (!state.framed) this.armManualFallback();

      if (state.framed && !this.cued) {
        this.cued = true;
        if (this.phase === "idle") playReadyCue();
      } else if (!state.framed) {
        this.cued = false;
      }
    },

    async loadChallenge() {
      this.challengeError = false;
      try {
        await this.$store.dispatch("flow/loadChallenge");
      } catch (e) {
        this.challengeError = true;
      }
    },

    requestAccess() {
      this.requesting = true;
      if (this.cameraActive) {
        // Retry after a denial — `active` is already true, so the watcher will not fire.
        this.$refs.recorder.acquire();
      } else {
        this.cameraActive = true;
      }
    },

    onCameraReady() {
      this.requesting = false;
      this.cameraReady = true;
      this.gate = "closed";
      this.$store.commit("ui/SET_MEDIA_GRANTED", true);
    },

    onFramed(framed) {
      this.framed = framed;
      if (framed) this.cancelManualFallback();
      else this.armManualFallback();
    },

    /** No detector on this device; see the same handler on the photo step. */
    onDetectorUnavailable() {
      this.cancelManualFallback();
      this.manualUnlocked = true;
    },

    /**
     * The framing check must never become a dead end — see the same guard on the photo
     * step. Here it matters more: a user who cannot get past this screen cannot finish
     * their verification at all.
     */
    armManualFallback() {
      if (this.manualUnlocked || this.fallbackTimer) return;
      this.fallbackTimer = setTimeout(() => {
        this.fallbackTimer = null;
        if (!this.framed) this.manualUnlocked = true;
      }, FRAMING_FALLBACK_MS);
    },

    cancelManualFallback() {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    },

    onCameraDenied() {
      this.requesting = false;
      this.cameraReady = false;
      // A silent attempt can fail merely because the browser wants a user gesture first,
      // and the recovery for that is the priming sheet, not settings instructions. Only
      // call it blocked once the request has failed from behind a tap.
      this.gate = this.primed ? "blocked" : "priming";
      if (this.primed) this.$store.commit("ui/SET_MEDIA_GRANTED", false);
    },

    start() {
      this.recorded = false;
      this.$refs.recorder.start();
    },

    stop() {
      this.$refs.recorder.stop();
    },

    retry() {
      this.recorded = false;
      this.cued = false;
      this.$refs.recorder.reset();
      // Back to a live preview, so the framing check — and its escape hatch — run again.
      this.armManualFallback();
    },

    /** The recorder finished assembling the clip; park it and allow continuing. */
    async onComplete({ blob, poster }) {
      await this.$store.dispatch("flow/saveVideo", { blob, poster });
      this.recorded = true;
    },

    confirm() {
      if (this.editing) {
        this.$router.push({ name: "check-assets" });
      } else {
        this.$router.push({ name: FLOW_ORDER[2] });
      }
    },
  },
};
</script>

<style scoped>
/* Only ever seen when the framing check gave up; it takes the place of the feedback that
   would otherwise be on the frame itself. */
.helper {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  color: var(--muted-foreground);
  font-size: 14px;
  text-align: center;
}

.helper >>> svg {
  width: 17px;
  height: 17px;
  flex: 0 0 auto;
}

/* Hero icon, one line of instruction, then the phrase set apart — in that order, because
   the phrase is what the user must still be able to read once recording starts. */
.actguide {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: var(--muted);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 16px 14px 15px;
}

.combo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.ar {
  color: var(--primary);
}

.ar >>> svg {
  width: 24px;
  height: 24px;
  display: block;
}

.face {
  width: 90px;
  height: 90px;
  flex: 0 0 auto;
}

.face img {
  width: 100%;
  height: 100%;
  display: block;
  border-radius: 16px;
  object-fit: contain;
  background: rgba(255, 255, 255, 0.1);
}

.ag-line {
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  color: var(--foreground);
  line-height: 1.65;
}

.ag-phrase {
  width: 100%;
  box-sizing: border-box;
  background: var(--primary-soft);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 13px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
}

/* High-contrast foreground, not green: this is the one thing that must be readable at
   arm's length while the user is also watching the camera. */
.ag-val {
  font-size: 21px;
  font-weight: 800;
  color: var(--foreground);
  line-height: 1.7;
  text-align: center;
  direction: rtl;
}

.ag-val.ag-loading {
  font-size: 14px;
  font-weight: 600;
  color: var(--muted-foreground);
}

.ag-val .pq {
  opacity: 0.4;
  font-weight: 800;
}

.ag-error {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  color: var(--muted-foreground);
}

.linkbtn {
  border: 0;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 700;
  color: var(--primary);
  cursor: pointer;
  text-decoration: underline;
}

.recbtn {
  min-height: 56px;
}

@media (prefers-reduced-motion: no-preference) {
  .ar.r {
    animation: arR 3s ease-in-out infinite;
  }
  .ar.l {
    animation: arL 3s ease-in-out infinite;
  }
  @keyframes arR {
    0%,
    100% {
      opacity: 1;
    }
    45%,
    55% {
      opacity: 0.35;
    }
  }
  @keyframes arL {
    0%,
    10%,
    90%,
    100% {
      opacity: 0.35;
    }
    50% {
      opacity: 1;
    }
  }
}
</style>
